import { useState, useRef, useCallback } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import {
  HiOutlineQrcode,
  HiOutlineDownload,
  HiOutlineRefresh,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import useDocumentTitle from '../hooks/useDocumentTitle'

const QR_SIZE = 256
const MAX_INPUT_LENGTH = 2000
const MAX_EXPORT_TEXT_LINES = 4
const PREVIEW_TEXT_LIMIT = 80

export default function QRCodePage() {
  useDocumentTitle('QR Generator')

  const [inputValue, setInputValue] = useState('')
  const [qrValue, setQrValue] = useState('')
  const [error, setError] = useState('')
  const canvasWrapperRef = useRef(null)

  const getWrappedTextLines = useCallback((ctx, text, maxWidth) => {
    const normalized = String(text ?? '').replace(/\r\n/g, '\n')
    if (!normalized) return []

    const cache = new Map()
    const measure = (t) => {
      if (!cache.has(t)) {
        cache.set(t, ctx.measureText(t).width)
      }
      return cache.get(t)
    }

    const splitLongWord = (word) => {
      const chunks = []
      let chunk = ''

      for (const ch of Array.from(word)) {
        const next = chunk + ch

        if (!chunk || measure(next) <= maxWidth) {
          chunk = next
        } else {
          chunks.push(chunk)
          chunk = ch
        }
      }

      if (chunk) chunks.push(chunk)
      return chunks
    }

    const wrapLine = (line) => {
      const words = line.split(/\s+/).filter(Boolean)
      if (!words.length) return ['']

      const lines = []
      let current = ''

      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word

        if (measure(candidate) <= maxWidth) {
          current = candidate
          continue
        }

        if (current) {
          lines.push(current)
          current = ''
        }

        if (measure(word) <= maxWidth) {
          current = word
          continue
        }

        const chunks = splitLongWord(word)
        if (!chunks.length) continue

        lines.push(...chunks.slice(0, -1))
        current = chunks[chunks.length - 1] ?? ''
      }

      if (current) lines.push(current)
      return lines
    }

    return normalized.split('\n').flatMap((p) => {
      const wrapped = wrapLine(p)
      return wrapped.length ? wrapped : ['']
    })
  }, [])

  const validate = useCallback((value) => {
    if (!value.trim()) {
      return 'Please enter a text or URL to generate a QR code.'
    }
    if (value.trim().length > MAX_INPUT_LENGTH) {
      return `Input is too long. Maximum ${MAX_INPUT_LENGTH} characters allowed.`
    }
    return ''
  }, [])

  const handleGenerate = () => {
    const validationError = validate(inputValue)
    if (validationError) {
      setError(validationError)
      return
    }
    setError('')
    setQrValue(inputValue.trim())
    toast.success('QR Code generated!')
  }

  const handleReset = () => {
    setInputValue('')
    setQrValue('')
    setError('')
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleGenerate()
    }
  }

  const handleDownloadPNG = () => {
    const wrapper = canvasWrapperRef.current
    if (!wrapper) return

    const canvas = wrapper.querySelector('canvas')
    if (!canvas) return

    const padding = 32
    const textGap = 16
    const fontSize = 16
    const lineHeight = 20

    // Measure wrapped text first (needs a 2D context)
    const exportCanvas = document.createElement('canvas')
    exportCanvas.width = canvas.width + padding * 2
    const ctx = exportCanvas.getContext('2d')
    if (!ctx) return

    ctx.font = `${fontSize}px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`
    const textMaxWidth = exportCanvas.width - padding * 2
    const allLines = getWrappedTextLines(ctx, qrValue, textMaxWidth)
    const fitWithEllipsis = (text, maxWidth, forceEllipsis = false) => {
      const ellipsis = '\u2026'
      const safeText = String(text ?? '').trimEnd()

      if (!safeText) {
        return forceEllipsis && ctx.measureText(ellipsis).width <= maxWidth ? ellipsis : ''
      }

      if (!forceEllipsis && ctx.measureText(safeText).width <= maxWidth) {
        return safeText
      }

      if (ctx.measureText(ellipsis).width > maxWidth) {
        return ''
      }

      let left = 0
      let right = safeText.length
      let result = ellipsis

      while (left <= right) {
        const mid = Math.floor((left + right) / 2)
        const candidate = safeText.slice(0, mid).trimEnd() + ellipsis

        if (ctx.measureText(candidate).width <= maxWidth) {
          result = candidate
          left = mid + 1
        } else {
          right = mid - 1
        }
      }

      return result
    }

    let lines = allLines
    if (lines.length > MAX_EXPORT_TEXT_LINES) {
      lines = lines.slice(0, MAX_EXPORT_TEXT_LINES)
      lines[lines.length - 1] = fitWithEllipsis(
        lines[lines.length - 1] ?? '',
        textMaxWidth,
        true
      )
    }

    const textBlockHeight = lines.length ? textGap + lines.length * lineHeight : 0
    exportCanvas.height = canvas.height + padding * 2 + textBlockHeight

    // White background
    ctx.fillStyle = '#FFFFFF'
    ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height)

    // Draw QR onto center
    ctx.drawImage(canvas, padding, padding)

    // Draw label below QR
    if (lines.length) {
      ctx.fillStyle = '#1a1a2e'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.font = `500 ${fontSize}px Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`

      const x = exportCanvas.width / 2
      let y = padding + canvas.height + textGap
      for (const line of lines) {
        ctx.fillText(line, x, y)
        y += lineHeight
      }
    }

    const link = document.createElement('a')
    link.download = `qrcode-${Date.now()}.png`
    link.href = exportCanvas.toDataURL('image/png')
    link.click()
    toast.success('QR Code downloaded as PNG')
  }

  const charCount = inputValue.length
  const isOverLimit = charCount > MAX_INPUT_LENGTH

  return (
    <div className="qrcode-page">
      <div className="page-header">
        <div>
          <h1>QR Code Generator</h1>
          <p>Generate a QR code from any text or URL</p>
        </div>
      </div>

      <div className="qrcode-layout">
        {/* Input Section */}
        <div className="form-card qrcode-input-section">
          <div className="form-section">
            <h3>
              <HiOutlineQrcode style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Enter Content
            </h3>
            <div className={`form-group full-width ${error ? 'has-error' : ''}`}>
              <label htmlFor="qr-input">
                Text or URL <span className="required">*</span>
              </label>
              <textarea
                id="qr-input"
                value={inputValue}
                onChange={(e) => {
                  setInputValue(e.target.value)
                  if (error) setError('')
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. https://example.com or any text..."
                rows={4}
                disabled={false}
                style={{ resize: 'vertical', minHeight: '100px' }}
              />
              <div className="qr-input-meta">
                {error && <span className="form-error">{error}</span>}
                <span
                  className={`char-count ${isOverLimit ? 'over-limit' : ''}`}
                >
                  {charCount} / {MAX_INPUT_LENGTH}
                </span>
              </div>
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '1.5rem' }}>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={!inputValue && !qrValue}
            >
              <HiOutlineRefresh />
              Reset
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={!inputValue.trim()}
            >
              <HiOutlineQrcode />
              Generate QR Code
            </button>
          </div>
        </div>

        {/* Preview Section */}
        <div className="form-card qrcode-preview-section">
          <div className="form-section">
            <h3>Preview</h3>
          </div>

          {qrValue ? (
            <div className="qrcode-result">
              <div className="qrcode-canvas-wrapper" ref={canvasWrapperRef}>
                <QRCodeCanvas
                  value={qrValue}
                  size={QR_SIZE}
                  bgColor="#FFFFFF"
                  fgColor="#1a1a2e"
                  level="H"
                  marginSize={2}
                />
              </div>

              <p className="qrcode-value-preview" title={qrValue}>
                {qrValue.length > PREVIEW_TEXT_LIMIT
                  ? `${qrValue.substring(0, PREVIEW_TEXT_LIMIT)}...`
                  : qrValue}
              </p>

              <div className="qrcode-download-actions">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleDownloadPNG}
                >
                  <HiOutlineDownload />
                  Download PNG
                </button>
              </div>
            </div>
          ) : (
            <div className="qrcode-placeholder">
              <HiOutlineQrcode className="qrcode-placeholder-icon" />
              <p>Your QR code will appear here</p>
              <span>Enter content and click "Generate QR Code"</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
